import { z } from "zod";

import { locationSchema } from "./packaging-solution";

const dimensionSchema = z.object({
	length: z.number(),
	width: z.number(),
	height: z.number(),
});
export type Dimension = z.infer<typeof dimensionSchema>;

const identifiersSchema = z
	.object({
		gtin: z.string().optional(),
		upc: z.string().optional(),
		ean: z.string().optional(),
		mpn: z.string().optional(),
		isbn: z.string().optional(),
		sku: z.string().optional(),
		itemNumber: z.string().optional(),
		gtiN_8: z.string().optional(),
		gtiN_12: z.string().optional(),
		gtiN_13: z.string().optional(),
		gtiN_14: z.string().optional(),
		isbN_10: z.string().optional(),
		isbN_13: z.string().optional(),
	})
	.catchall(z.string().optional());

const nestingSchema = z.object({
	nestGrowthDimension1: z.number(),
	nestGrowthDimension2: z.number(),
	nestGrowthDimension3: z.number(),
	nestMax: z.number(),
});
export type Nesting = z.infer<typeof nestingSchema>;

const dimAuditSchema = z.object({
	created: z.string(),
	updated: z.string(),
	lastUpdatedById: z.string(),
	totalTimesUpdated: z.number(),
});

const productSchema = z
	.object({
		tenantId: z.string().optional(),
		identifier: z.string().nullish(),
		identifiers: identifiersSchema.optional(),
		name: z.string().nullish(),
		description: z.string().optional(),
		weight: z.number().optional(),
		requiresPadding: z.boolean().optional(),
		useDefaultPadding: z.boolean().optional(),
		padding: z.number().optional(),
		customAttributes: z.record(z.string(), z.string()).optional(),
		dimensions: dimensionSchema.optional(),
		paddedDimensions: dimensionSchema.optional(),
		canContain: z.boolean().optional(),
		voidFill: dimensionSchema.optional(),
		canNest: z.boolean().optional(),
		nesting: nestingSchema.optional(),
		orientationLocked: z.boolean().optional(),
		orientation: z.unknown().optional(),
		quantity: z.number().optional(),
		isSingleLayer: z.boolean().optional(),
		audit: dimAuditSchema.optional(),
		productImage: z.string().optional(),
		supplier: z.string().optional(),
		supplierNumber: z.number().optional(),
		manufacturer: z.string().optional(),
		comment: z.string().optional(),
	})
	.catchall(z.unknown());
export type Product = z.infer<typeof productSchema>;

const validAttributeTypes = [
	"Bool",
	"Boolean",
	"Number",
	"String",
	"Date",
	"ImageUrl",
	"Select",
	"Weight",
	"Padding",
	"Dimensions",
	"Nesting",
	"Orientation",
	"Cavity",
	"VoidFill",
] as const;
const validAttributeTypeSchema = z.enum(validAttributeTypes);
export type ValidAttributeType = z.infer<typeof validAttributeTypeSchema>;

export const attributeTypeInfoSchema = z.object({
	disabled: z.boolean(),
	exampleText: z.string().nullable().optional(),
	id: z.string(),
	isCustomAttribute: z.boolean(),
	isRequired: z.boolean(),
	maxLength: z.union([z.number(), z.string()]).nullable().optional(),
	name: z.string(),
	options: z.array(z.string()).nullable().optional(),
	prompt: z.string().nullable().optional(),
	type: validAttributeTypeSchema,
});
export type AttributeTypeInfo = z.infer<typeof attributeTypeInfoSchema>;

const _stepWithConfigSchema = attributeTypeInfoSchema.extend({
	isConfigured: z.boolean(),
});
export type StepWithConfig = z.infer<typeof _stepWithConfigSchema>;

const _createCustomAttributeRequestSchema = attributeTypeInfoSchema.pick({
	name: true,
	type: true,
	prompt: true,
	maxLength: true,
	options: true,
});
export type CreateCustomAttributeRequest = z.infer<typeof _createCustomAttributeRequestSchema>;

const _createCustomAttributeResponseSchema = z.object({
	data: attributeTypeInfoSchema.extend({
		tenantId: z.string(),
		createdDate: z.string(),
	}),
});
export type CreateCustomAttributeResponse = z.infer<typeof _createCustomAttributeResponseSchema>;

const mediaSchema = z.object({
	dimensions: dimensionSchema.nullish(),
	thickness: z.number().nullish(),
	style: z.string().nullish(),
	brand: z.string().nullish(),
	cost: z.number().nullish(),
	attributes: z.record(z.string(), z.string()).nullish(),
});
export type Media = z.infer<typeof mediaSchema>;

const itemToPackSchema = z.object({
	productId: z.string(),
	quantity: z.number().optional(),
	dimensions: dimensionSchema.optional(),
});
export type ItemToPack = z.infer<typeof itemToPackSchema>;

const _cartonRequestSchema = z.object({
	orderId: z.string(),
	brand: z.string().optional(),
	cartonDesign: z.string().optional(),
	licensePlateNumber: z.string().optional(),
	productionGroup: z.string().optional(),
	machineGroup: z.string().optional(),
	machine: z.string().optional(),
	packstation: z.string().optional(),
	xValues: z.record(z.string(), z.string()).optional(),
	itemsToPack: z.array(itemToPackSchema),
});
export type CartonRequest = z.infer<typeof _cartonRequestSchema>;

const _cartonCreateResultSchema = z.object({
	response: z.string().optional(),
});
export type CartonCreateResult = z.infer<typeof _cartonCreateResultSchema>;

// Identifier types for dim-config-api
const validIdentifierTypes = [
	"Unconfigured",
	"UPC",
	"GTIN",
	"GTIN_8",
	"GTIN_12",
	"GTIN_13",
	"GTIN_14",
	"EAN",
	"MPN",
	"ISBN",
	"ISBN_10",
	"ISBN_13",
	"SKU",
	"ItemNumber",
] as const;
const identifierTypeSchema = z.enum(validIdentifierTypes);
export type IdentifierType = z.infer<typeof identifierTypeSchema>;

export function isValidIdentifierType(value: unknown): value is IdentifierType {
	return typeof value === "string" && validIdentifierTypes.includes(value as IdentifierType);
}

export const supportedIdentifierSchema = z.object({
	key: z.string().optional(),
	name: z.string().optional(),
	isGlobal: z.boolean(),
});
export type SupportedIdentifier = z.infer<typeof supportedIdentifierSchema>;

const defaultCartonSchema = z.object({
	media: mediaSchema.optional(),
	location: locationSchema.optional(),
});
export type DefaultCarton = z.infer<typeof defaultCartonSchema>;

const productionSettingsSchema = z.object({
	aliasRequired: z.boolean(),
	automaticAttributeCollection: z.boolean(),
	automaticCartonCreation: z.boolean(),
	automaticDistoScanSubmission: z.boolean(),
	cartonSettings: defaultCartonSchema.optional(),
	clearDistoAtLargeSize: z.boolean(),
	clearMeasurement: z.number().optional(),
	defaultPadding: z.number(),
	distoOffset: z.number(),
	lpnRequired: z.boolean(),
});
export type ProductionSettings = z.infer<typeof productionSettingsSchema>;

export const tenantConfigurationSchema = z.object({
	attributeCaptureSteps: z.array(attributeTypeInfoSchema).optional(),
	id: z.string(),
	lastUpdated: z.string(),
	mainIdentifierType: identifierTypeSchema,
	productionSettings: productionSettingsSchema,
	scanSteps: z.array(attributeTypeInfoSchema).optional(),
	totalProductCount: z.number(),
});
export type TenantConfiguration = z.infer<typeof tenantConfigurationSchema>;

// Types for dim-product-api
const _tenantConfigSchema = z.object({
	attributeCaptureSteps: z.array(attributeTypeInfoSchema),
	mainIdentifierType: z.string(),
});
export type TenantConfig = z.infer<typeof _tenantConfigSchema>;

const importStatusValueSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
export type ImportStatusValue = z.infer<typeof importStatusValueSchema>;

export const IMPORT_STATUS = {
	IMPORTING: 0,
	COMPLETE: 1,
	FAILED: 2,
	INCOMPLETE: 3,
} as const;

export const importStatusSchema = z.object({
	mainId: z.string(),
	filename: z.string(),
	created: z.string(),
	updated: z.string().optional(),
	status: importStatusValueSchema,
	totalCount: z.number(),
	processingRecordNumber: z.number(),
	errors: z.array(z.string()),
});
export type ImportStatus = z.infer<typeof importStatusSchema>;

export const productSearchResultsSchema = z.object({
	reason: z.string().optional().nullable(),
	isFound: z.boolean(),
	products: z.array(productSchema).optional(),
	totalCount: z.number().optional(),
	pageNumber: z.number().optional(),
	pageSize: z.number().optional(),
});
export type ProductSearchResults = z.infer<typeof productSearchResultsSchema>;

const _productCountSchema = z.object({
	count: z.number(),
});
export type ProductCount = z.infer<typeof _productCountSchema>;

export const productLookupResultSchema = z.object({
	reason: z.string().optional(),
	isFound: z.boolean(),
	product: productSchema.optional(),
	mainId: z.string().optional(),
});
export type ProductLookupResult = z.infer<typeof productLookupResultSchema>;

// Types for dim-scan-api
const productRequestSchema = z.object({
	quantity: z.number(),
	content: productSchema.optional(),
});
export type ProductRequest = z.infer<typeof productRequestSchema>;

const _scanToProduceRequestSchema = z.object({
	alias: z.string().optional(),
	licensePlateNumber: z.string().optional(),
	location: locationSchema.optional(),
	brand: z.string().optional(),
	cartonDesign: z.string().optional(),
	quantity: z.number(),
	xValues: z.record(z.string(), z.string()).optional(),
	products: z.array(productRequestSchema).optional(),
	mainIdentifierType: z.string().optional(),
});
export type ScanToProduceRequest = z.infer<typeof _scanToProduceRequestSchema>;

const baseResultSchema = z.object({
	isSuccessful: z.boolean(),
	errors: z.array(z.string()).optional(),
});

const _scanToProduceResultSchema = baseResultSchema.extend({
	alias: z.string().optional(),
	reason: z.string().optional(),
});
export type ScanToProduceResult = z.infer<typeof _scanToProduceResultSchema>;
